export type DimensionUnit = "in" | "mm" | "cm" | "ft";
export type CapacityUnit = "gal" | "L" | "lbs";

export interface DimensionsLike {
  length?: number;
  width?: number;
  height?: number;
  capacity?: number;
  unit?: DimensionUnit;
  capacityUnit?: CapacityUnit;
  raw?: string;
}

const INCHES_PER_UNIT: Record<DimensionUnit, number> = {
  in: 1,
  ft: 12,
  cm: 1 / 2.54,
  mm: 1 / 25.4,
};

export function convertDimensionMagnitude(
  value: number,
  from: DimensionUnit,
  to: DimensionUnit,
): number {
  if (from === to) return value;
  return (value * INCHES_PER_UNIT[from]) / INCHES_PER_UNIT[to];
}

const SORT_SIDE_STEPS_PER_INCH = 10;
const SORT_SIDE_RADIX = 10_000;

function isPositiveSide(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value > 0;
}

function sortSide(value: number | undefined, unit: DimensionUnit): number {
  if (!isPositiveSide(value)) return 0;
  const steps = Math.round(
    convertDimensionMagnitude(value, unit, "in") * SORT_SIDE_STEPS_PER_INCH,
  );
  return Math.min(Math.max(steps, 1), SORT_SIDE_RADIX - 1);
}

export function dimensionsSortKey(
  dims: DimensionsLike | null | undefined,
): number | undefined {
  if (!dims || !isPositiveSide(dims.length) || !isPositiveSide(dims.width)) {
    return undefined;
  }
  const unit = dims.unit ?? "in";
  const length = sortSide(dims.length, unit);
  const width = sortSide(dims.width, unit);
  const height = sortSide(dims.height, unit);
  return (length * SORT_SIDE_RADIX + width) * SORT_SIDE_RADIX + height;
}

export function formatDimensionsSummary(
  dims: DimensionsLike | null | undefined,
): string {
  if (!dims) return "";
  const hasPhysical = isPositiveSide(dims.length) && isPositiveSide(dims.width);
  const physical = hasPhysical
    ? `${[dims.length, dims.width, dims.height]
        .filter(isPositiveSide)
        .join(" × ")} ${dims.unit ?? "in"}`
    : "";
  if (typeof dims.capacity === "number") {
    const capacity = `${dims.capacity} ${dims.capacityUnit ?? "gal"}`;
    return physical ? `${capacity} (${physical})` : capacity;
  }
  if (physical) return physical;
  return dims.raw?.trim() || "";
}

export function dimensionsSignature(
  dims: DimensionsLike | null | undefined,
): string {
  if (!dims) return "";
  return [
    dims.length ?? "",
    dims.width ?? "",
    dims.height ?? "",
    dims.capacity ?? "",
    dims.unit ?? "",
    dims.capacityUnit ?? "",
    dims.raw?.trim() || "",
  ].join("|");
}
